import { Button, ButtonGroup, Checkbox, Chip, FormControlLabel, FormGroup } from '@mui/material';
import { ColDef, EditableCallbackParams, GetRowIdParams, ICellRendererParams } from 'ag-grid-community';
import { useSnackbar } from 'notistack';
import React, { useEffect, useMemo, useState } from 'react';
import { MarkItViewApi } from '../../../../Apis/Apis';
import { CommodityManagementSiteAccessModel, CommodityManagementSiteUserModel, CommodityManagementUserPermissions } from '../../../../Generated/Mark-It-View-Api';
import { EnumHelper } from '../../../../Helpers';
import { RootState, useApplicationSelector } from '../../../../Redux/ReduxStore';
import { StoneXGrid, StoneXRow } from '../../../StoneX';
import { StoneXButton, StoneXCheckbox, StoneXModal, StoneXTextField } from '../../../StoneXMui';
import { UserAccessState } from '../../../../Redux/Slices/UserAccessSlice';
import { useLoadingState } from '../../../../Hooks';

export default function UserManagementAdminSettings() {

  const userAccess = useApplicationSelector((state: RootState): UserAccessState => state.userAccess);
  const activeSite = useApplicationSelector((state: RootState): CommodityManagementSiteAccessModel => state.activeSite.site);
  const siteAccess = userAccess.commodityManagementUserSiteAccesses.find(s => s.siteGuid === activeSite.siteGuid);
  const { enqueueSnackbar } = useSnackbar();
  const updateUserLoadingState = useLoadingState();

  const [siteUsers, setSiteUsers] = useState<CommodityManagementSiteUserModel[]>([]);
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [confirmMakeAdmin, setConfirmMakeAdmin] = useState<CommodityManagementSiteUserModel | null>(null);
  const [confirmRevokeAdmin, setConfirmRevokeAdmin] = useState<CommodityManagementSiteUserModel | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<CommodityManagementSiteUserModel | null>(null);

  const permissionKeys = useMemo(() => Object.values(CommodityManagementUserPermissions), []);

  useEffect(() => {
    MarkItViewApi.listCommodityManagementSiteUsers(null, activeSite.siteGuid!).then((res) => setSiteUsers(res.data.rows ?? []));
  }, []);

  function addUser(email: string, firstName: string, lastName: string, isSiteManager: boolean, permissions: CommodityManagementUserPermissions[]) {
    MarkItViewApi.addCommodityManagementUserSiteAccess(
      updateUserLoadingState.setLoadingState,
      activeSite.siteGuid!,
      '',
      email,
      firstName,
      lastName,
      isSiteManager,
      permissions,
    ).then((res) => {
      MarkItViewApi.listCommodityManagementSiteUsers(null, activeSite.siteGuid!).then(r => setSiteUsers(r.data.rows ?? []));
      enqueueSnackbar(`User added successfully`, { variant: 'success' });
      setAddUserModalOpen(false);
    }).catch(() => {
      enqueueSnackbar('Failed to add user', { variant: 'error' });
    });
  }

  function onPermissionChange(user: CommodityManagementSiteUserModel, permission: CommodityManagementUserPermissions, checked: boolean) {
    const newPermissions = checked
      ? [...user.permissions, permission]
      : user.permissions.filter(p => p !== permission);
    const updatedUser = { ...user, permissions: newPermissions };
    setSiteUsers(prev => prev.map(u => u.marketViewUserGuid === user.marketViewUserGuid ? updatedUser : u));
    MarkItViewApi.updateCommodityManagementUserSiteAccess(
      null,
      activeSite.siteGuid!,
      user.marketViewUserGuid,
      user.isSiteManager,
      newPermissions,
    ).catch(() => {
      enqueueSnackbar('Failed to update permissions', { variant: 'error' });
      setSiteUsers(prev => prev.map(u => u.marketViewUserGuid === user.marketViewUserGuid ? user : u));
    });
  }

  function makeAdmin(user: CommodityManagementSiteUserModel) {
    MarkItViewApi.updateCommodityManagementUserSiteAccess(
      updateUserLoadingState.setLoadingState,
      activeSite.siteGuid!,
      user.marketViewUserGuid,
      true,
      Object.values(CommodityManagementUserPermissions),
    ).then(() => {
      setSiteUsers(prev => prev.map(u => u.marketViewUserGuid === user.marketViewUserGuid ? { ...u, isSiteManager: true, permissions: Object.values(CommodityManagementUserPermissions) } : u));
      enqueueSnackbar(`${user.displayName} is now a site admin`, { variant: 'success' });
      setConfirmMakeAdmin(null);
    }).catch(() => {
      enqueueSnackbar('Failed to make admin', { variant: 'error' });
    });
  }

  function revokeAdmin(user: CommodityManagementSiteUserModel) {
    MarkItViewApi.updateCommodityManagementUserSiteAccess(
      updateUserLoadingState.setLoadingState,
      activeSite.siteGuid!,
      user.marketViewUserGuid,
      false,
      user.permissions,
    ).then(() => {
      setSiteUsers(prev => prev.map(u => u.marketViewUserGuid === user.marketViewUserGuid ? { ...u, isSiteManager: false } : u));
      enqueueSnackbar(`${user.displayName}'s site admin access has been revoked`, { variant: 'success' });
      setConfirmRevokeAdmin(null);
    }).catch(() => {
      enqueueSnackbar('Failed to revoke admin', { variant: 'error' });
    });
  }

  function removeUser(user: CommodityManagementSiteUserModel) {
    MarkItViewApi.deleteCommodityManagementUserSiteAccess(
      updateUserLoadingState.setLoadingState,
      activeSite.siteGuid!,
      user.marketViewUserGuid,
    ).then(() => {
      setSiteUsers(prev => prev.filter(u => u.marketViewUserGuid !== user.marketViewUserGuid));
      enqueueSnackbar(`${user.displayName} has been removed`, { variant: 'success' });
      setConfirmRemove(null);
    }).catch(() => {
      enqueueSnackbar('Failed to remove user', { variant: 'error' });
    });
  }

  const columnDefs: ColDef[] = [
    {
      headerName: 'User',
      field: 'displayName',
      sortable: true,
      flex: 1,
    },
    {
      headerName: 'Email',
      field: 'email',
      sortable: true,
      flex: 1.5,
      minWidth: 190,
    },
    {
      headerName: 'Admin',
      field: 'isSiteManager',
      sortable: true,
      width: 125,
      cellRenderer: (params: ICellRendererParams<CommodityManagementSiteUserModel>) => {
        if (params.data?.isAdministrator) {
          return <Chip variant="success" label="ADMIN" size="small" />;
        }
        else if (params.data?.isSiteManager) {
          return <Chip variant="outlined" label="SITE ADMIN" size="small" />;
        }
        else {
          return <></>
        }
      }
    },
    ...permissionKeys.map(perm => ({
      headerName: EnumHelper.enumToString(perm).replace('Manage', '').trim(),
      field: perm,
      width: 90,
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      editable: (params: EditableCallbackParams) => !params.data.isSiteManager,
      valueGetter: (params: any) => params.data.isSiteManager || params.data.permissions?.includes(perm),
      valueSetter: (params: any) => {
        const checked = params.newValue;
        const permissions = params.data.permissions || [];
        if (checked && !permissions.includes(perm)) {
          params.data.permissions = [...permissions, perm];
        } else if (!checked && permissions.includes(perm)) {
          params.data.permissions = permissions.filter((p: string) => p !== perm);
        }
        
        onPermissionChange(params.data, perm, checked);

        return true;
      },
    })),
    {
      headerName: 'Actions',
      width: 225,
      sortable: false,
      cellRenderer: (params: ICellRendererParams<CommodityManagementSiteUserModel>) => {
        const user = params.data!;
        if (userAccess.isAdmin) {
          // Admins: toggle Make Admin / Revoke Admin + Remove (cannot act on other admins)
          return (
            <ButtonGroup variant="text">
              {user.isSiteManager
                ? <Button disabled={user.isAdministrator} onClick={() => setConfirmRevokeAdmin(user)}>Revoke Admin</Button>
                : <Button disabled={user.isAdministrator} onClick={() => setConfirmMakeAdmin(user)}>Make Admin</Button>
              }
              <Button disabled={user.isAdministrator} onClick={() => setConfirmRemove(user)}>Remove</Button>
            </ButtonGroup>
          );
        }
        if (siteAccess?.isSiteManager) {
          // Site managers: can promote regular users; can remove regular users only
          return (
            <ButtonGroup variant="text">
              <Button disabled={user.isSiteManager || user.isAdministrator} onClick={() => setConfirmMakeAdmin(user)}>Make Admin</Button>
              <Button disabled={user.isSiteManager || user.isAdministrator} onClick={() => setConfirmRemove(user)}>Remove</Button>
            </ButtonGroup>
          );
        }
        return null;
      },
    },
  ];

  return (
    <>
      <StoneXRow align="end">
        <h3>Users</h3>
        <Button className="pull-right" variant="outlined" onClick={() => setAddUserModalOpen(true)}>Add</Button>
      </StoneXRow>

      <StoneXGrid
        hideGridOptions
        getRowId={(params: GetRowIdParams) => params.data.marketViewUserGuid}
        columns={columnDefs}
        rows={siteUsers}
      />

      {/* Make Admin confirmation */}
      <StoneXModal header="Make Site Admin" open={!!confirmMakeAdmin} onClose={() => setConfirmMakeAdmin(null)}>
        <StoneXRow>
          Are you sure you want to make <strong>&nbsp;{confirmMakeAdmin?.displayName}&nbsp;</strong> a site admin?
        </StoneXRow>
        <StoneXRow justify="end" marginDirection="none">
          <StoneXButton variant="outlined" onClick={() => setConfirmMakeAdmin(null)}>Cancel</StoneXButton>
          <StoneXButton isWorking={updateUserLoadingState.isLoading()} onClick={() => makeAdmin(confirmMakeAdmin!)}>Make Admin</StoneXButton>
        </StoneXRow>
      </StoneXModal>

      {/* Revoke Admin confirmation */}
      <StoneXModal header="Revoke Site Admin" open={!!confirmRevokeAdmin} onClose={() => setConfirmRevokeAdmin(null)}>
        <StoneXRow>
          Are you sure you want to revoke site admin access from <strong>&nbsp;{confirmRevokeAdmin?.displayName}&nbsp;</strong>?
        </StoneXRow>
        <StoneXRow justify="end" marginDirection="none">
          <StoneXButton variant="outlined" onClick={() => setConfirmRevokeAdmin(null)}>Cancel</StoneXButton>
          <StoneXButton isWorking={updateUserLoadingState.isLoading()} onClick={() => revokeAdmin(confirmRevokeAdmin!)}>Revoke Admin</StoneXButton>
        </StoneXRow>
      </StoneXModal>

      {/* Remove confirmation */}
      <StoneXModal header="Remove User" open={!!confirmRemove} onClose={() => setConfirmRemove(null)}>
        <StoneXRow>
          Are you sure you want to remove <strong>&nbsp;{confirmRemove?.displayName}&nbsp;</strong> from this site?
        </StoneXRow>
        <StoneXRow justify="end" marginDirection="none">
          <StoneXButton variant="outlined" onClick={() => setConfirmRemove(null)}>Cancel</StoneXButton>
          <StoneXButton isWorking={updateUserLoadingState.isLoading()} onClick={() => removeUser(confirmRemove!)}>Remove</StoneXButton>
        </StoneXRow>
      </StoneXModal>

      <AddUserModal
        open={addUserModalOpen}
        onClose={() => setAddUserModalOpen(false)}
        onAdd={addUser}
        permissionKeys={permissionKeys}
        isWorking={updateUserLoadingState.isLoading()}
      />
    </>
  );
}

interface AddUserModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (email: string, firstName: string, lastName: string, isSiteManager: boolean, permissions: CommodityManagementUserPermissions[]) => void;
  permissionKeys: CommodityManagementUserPermissions[];
  isWorking: boolean;
}

function AddUserModal({ open, onClose, onAdd, permissionKeys, isWorking }: AddUserModalProps) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isSiteManager, setIsSiteManager] = useState(false);
  const [permissions, setPermissions] = useState<CommodityManagementUserPermissions[]>([]);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setFirstName('');
      setLastName('');
      setIsSiteManager(false);
      setPermissions([]);
    }
  }, [open]);

  function togglePermission(perm: CommodityManagementUserPermissions, checked: boolean) {
    setPermissions(prev => checked ? [...prev, perm] : prev.filter(p => p !== perm));
  }

  function handleSiteManagerChange(checked: boolean) {
    setIsSiteManager(checked);
    if (checked) setPermissions(permissionKeys);
    else setPermissions([]);
  }

  function isValidEmail(value: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  function isValidName(value: string) {
    return value.trim().length >= 2;
  }

  const canSubmit = isValidEmail(email) && isValidName(firstName) && isValidName(lastName);

  function handleAdd() {
    onAdd(email, firstName.trim(), lastName.trim(), isSiteManager, permissions);
  }

  return (
    <StoneXModal header="Add User" open={open} onClose={onClose} width="450px">
      <StoneXRow>
        <StoneXTextField label="Email" value={email} onUpdate={setEmail} error={email.length > 0 && !isValidEmail(email) ? "Email address is invalid" : undefined}/>
      </StoneXRow>
      <StoneXRow>
        <StoneXTextField label="First Name" maxLength={50} value={firstName} onUpdate={setFirstName} error={firstName.length > 0 && !isValidName(firstName) ? "Must be at least 2 characters" : undefined}/>
        <StoneXTextField label="Last Name" maxLength={50} value={lastName} onUpdate={setLastName} error={lastName.length > 0 && !isValidName(lastName) ? "Must be at least 2 characters" : undefined}/>
      </StoneXRow>
      <StoneXRow>
        <FormGroup>
          <StoneXCheckbox
            label="Site Admin"
            checked={isSiteManager}
            onUpdate={(e) => handleSiteManagerChange(e)}
          />
          {permissionKeys.map(perm => (
            <StoneXCheckbox
              key={perm}
              label={EnumHelper.enumToString(perm).replace('Manage', '').trim()} 
              checked={isSiteManager || permissions.includes(perm)}
              onUpdate={(e) => togglePermission(perm, e)}
            />
          ))}
        </FormGroup>
      </StoneXRow>
      <StoneXRow justify="end">
        <StoneXButton variant="outlined" onClick={onClose}>Cancel</StoneXButton>
        <StoneXButton isWorking={isWorking} onClick={handleAdd} disabled={!canSubmit}>Add User</StoneXButton>
      </StoneXRow>
    </StoneXModal>
  );
}
